import { useId } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SectionLabel } from '@/components/ui/Title.tsx';

interface SectionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** Dopisek w pasku, np. „3 pozycje” zwinięte albo zdanie o zakresie. */
  hint?: React.ReactNode;
  /** Podsumowanie po prawej — sformatowana kwota albo licznik. */
  summary?: React.ReactNode;
  /**
   * Stan przychodzi z zewnątrz, bo ekrany sterują wszystkimi sekcjami naraz
   * („rozwiń wszystkie”, filtr pozycji) — karta go tylko odwzorowuje.
   */
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Zwijana sekcja-karta: pasek z etykietą i podsumowaniem, pod nim treść.
 * Treść jest rodzeństwem paska, nie jego wnętrzem, więc może nieść własne
 * przyciski.
 */
const SectionCard = ({
  title,
  hint,
  summary,
  isOpen,
  onToggle,
  className,
  children,
  ...props
}: SectionCardProps) => {
  const panelId = useId();

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-neutral-200 bg-basic-white',
        className,
      )}
      {...props}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={cn(
          'flex w-full cursor-pointer flex-wrap items-center gap-x-2.5 bg-neutral-50 px-[18px] py-[13px] text-left',
          isOpen && 'border-b border-neutral-100',
        )}
      >
        <ChevronRight
          aria-hidden
          className={cn(
            'size-3.5 shrink-0 text-neutral-500 transition-transform duration-150',
            isOpen && 'rotate-90',
          )}
        />
        <SectionLabel>{title}</SectionLabel>
        {hint && <span className="text-[13px] text-neutral-500">{hint}</span>}
        {summary && (
          <span className="ml-auto whitespace-nowrap text-[13px] font-semibold tabular-nums text-neutral-700">
            {summary}
          </span>
        )}
      </button>

      {isOpen && <div id={panelId}>{children}</div>}
    </div>
  );
};

export default SectionCard;
